import React, { useEffect } from 'react';
import { useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CustomDatePicker from 'common-ui/customDatePicker';
import MobileList from 'common-ui/mobileList';
import { AsyncSelect } from 'common-ui/select';
import { Table } from 'common-ui/table';
import { TICKET_APPOINTMENT_SERVICE_OPTIONS } from 'constants/constants';
import dayjs from 'dayjs';
import { getCustomersAppointments } from 'features/customers/customersActions';
import {
  selectCustomerAppointmentLength,
  selectCustomerAppointmentLoading,
  selectCustomerAppointments,
} from 'features/customers/customersSlice';
import AvatarGroup from 'pages/components/avatarGroup';
import { formatDateTime } from 'utils/dateUtils';

import {
  ExpandableWrapper,
  ExpandedLabel,
  ExpandedValue,
  Row,
} from '../../../userManagement/UserManagement.styles';
import {
  ClearAllText,
  ClickableTicketId,
  CreatedDateBadge,
  CustomDatePickerWrapper,
  FiltersWrapper,
  FiltersWrapperDiv,
  Line,
} from './Components.styles';

const AppointmentHistory = ({ appointmentId }) => {
  const limit = 10;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const count = useSelector(selectCustomerAppointmentLength);
  const pagesCount = useMemo(() => Math.ceil(count / limit), [count]);
  const data = useSelector(selectCustomerAppointments);
  const loading = useSelector(selectCustomerAppointmentLoading);
  const [fieldData, setFieldData] = useState({
    service: [],
    createdFrom: null,
    createdTo: null,
    offset: 0,
  });

  const currentPage = useMemo(() => {
    return Math.floor((fieldData.offset || 0) / limit) + 1;
  }, [fieldData.offset, fieldData.limit]);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (_, data) => (
        <ClickableTicketId
          onClick={() => navigate(`/project-management/appointment/${data?.uuid}`)}
        >
          <p>{data?.id}</p>
        </ClickableTicketId>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (_, data) => <>{data?.location || '-'}</>,
    },
    {
      title: 'New Offer',
      dataIndex: 'newOffers',
      key: 'newOffers',
      render: (_, data) => <>{data?.selectedOffer || '-'}</>,
    },
    {
      title: 'Assignee',
      dataIndex: 'assignees',
      key: 'assignees',
      render: (_, data) => <AvatarGroup data={data?.assignees || []} maxVisible={4} />,
    },
    {
      title: 'Timestamp',
      dataIndex: 'date',
      key: 'date',
      render: (_, data) => (
        <CreatedDateBadge>
          <p>{formatDateTime(data?.date)}</p>
        </CreatedDateBadge>
      ),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (_, data) => (
        <div>
          <p>
            {data?.createdBy?.name
              ? `${data.createdBy.name} ${data.createdBy.surname || ''}`
              : '-'}
          </p>
        </div>
      ),
    },
  ];

  const mobileColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (_, data) => (
        <ClickableTicketId
          onClick={() => navigate(`/project-management/appointment/${data?.uuid}`)}
        >
          <p>{data?.id}</p>
        </ClickableTicketId>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (_, data) => <>{data?.location || '-'}</>,
    },
    {
      title: 'Timestamp',
      dataIndex: 'date',
      key: 'date',
      render: (_, data) => (
        <CreatedDateBadge>
          <p>{formatDateTime(data?.date)}</p>
        </CreatedDateBadge>
      ),
    },
  ];

  const handleSearchChange = (field, value) => {
    setFieldData((prev) => ({
      ...prev,
      [field]: value,
      offset: 0,
    }));
  };

  const handleDateChange = (field, value) => {
    setFieldData((prev) => ({
      ...prev,
      [field]: value,
      offset: 0,
    }));
  };

  const handleClearAll = () => {
    setFieldData({
      service: [],
      createdFrom: null,
      createdTo: null,
      offset: 0,
    });
  };

  const onPaginationChange = (page) => {
    setFieldData((prev) => ({
      ...prev,
      offset: (page - 1) * limit,
    }));
  };

  useEffect(() => {
    if (appointmentId) {
      dispatch(
        getCustomersAppointments({
          customerId: appointmentId,
          ...fieldData,
          limit: limit,
          offset: fieldData.offset,
          services: fieldData.service?.map((item) => item.value) || [],
          createdFrom: formatDateTime(fieldData.createdFrom, true),
          createdTo: formatDateTime(fieldData.createdTo, true),
        })
      );
    }
  }, [appointmentId, fieldData, dispatch]);

  const renderExpandableContent = (row) => {
    return (
      <ExpandableWrapper>
        <Row>
          <ExpandedLabel>New Offer</ExpandedLabel>
          <ExpandedValue>{row?.newOffers || '-'}</ExpandedValue>
        </Row>
        <Row>
          <ExpandedLabel>Assignee</ExpandedLabel>
          <ExpandedValue>
            {row?.assignees?.length > 0 ? (
              <AvatarGroup data={row?.assignees} maxVisible={4} />
            ) : (
              '-'
            )}
          </ExpandedValue>
        </Row>
        <Row>
          <ExpandedLabel>Created By</ExpandedLabel>
          <ExpandedValue>
            {row?.createdBy?.name
              ? `${row.createdBy.name} ${row.createdBy.surname || ''}`
              : '-'}
          </ExpandedValue>
        </Row>
      </ExpandableWrapper>
    );
  };

  return (
    <>
      <FiltersWrapper>
        <FiltersWrapperDiv>
          <AsyncSelect
            showItemsContainer={false}
            placeholder="Service"
            isClearable
            isSearchable={false}
            isMulti
            hideSelectedOptions={false}
            value={fieldData.service}
            defaultOptions={TICKET_APPOINTMENT_SERVICE_OPTIONS}
            loadOptions={(_inputValue, callback) => {
              callback(TICKET_APPOINTMENT_SERVICE_OPTIONS);
            }}
            onChange={(value) => handleSearchChange('service', value)}
          />

          <CustomDatePickerWrapper>
            <CustomDatePicker
              value={fieldData.createdFrom}
              onChange={(date) => handleDateChange('createdFrom', date)}
              placeholder="Created from"
              disableFuture
              maxDate={fieldData.createdTo ? dayjs(fieldData.createdTo) : null}
            />
          </CustomDatePickerWrapper>

          <CustomDatePickerWrapper>
            <CustomDatePicker
              value={fieldData.createdTo}
              onChange={(date) => handleDateChange('createdTo', date)}
              placeholder="Created to"
              disableFuture
              minDate={fieldData.createdFrom ? dayjs(fieldData.createdFrom) : null}
            />
          </CustomDatePickerWrapper>
        </FiltersWrapperDiv>
        <Line>
          <ClearAllText onClick={handleClearAll}>Clear all</ClearAllText>
        </Line>
      </FiltersWrapper>

      <Table
        loading={loading}
        columns={columns}
        data={data}
        onPaginationChange={onPaginationChange}
        totalPages={pagesCount}
        currentPage={currentPage}
      />
      <MobileList columns={mobileColumns} data={data} expandable={renderExpandableContent} />
    </>
  );
};

export default AppointmentHistory;
